// src/v3/ui/_common/components/ui/toast.tsx

import { AlertCircle, CheckCircle2, Info, X } from "lucide-react";
import { useEffect } from "react";
import { Button } from "./button";

const TOAST_VARIANT_OBJ = {
  SUCCESS: "success",
  ERROR: "error",
  INFO: "info",
} as const;
export type ToastVariant = (typeof TOAST_VARIANT_OBJ)[keyof typeof TOAST_VARIANT_OBJ];

export interface ToastData {
  id: string;
  variant: ToastVariant;
  message: string;
  retryAction?: (() => void) | undefined;
}

interface ToastProps {
  toast: ToastData;
  onDismiss: (id: string) => void;
}

const variantConfig: Record<
  ToastVariant,
  { icon: typeof CheckCircle2; className: string }
> = {
  success: {
    icon: CheckCircle2,
    className: "border-green-500 bg-green-50 text-green-900",
  },
  error: {
    icon: AlertCircle,
    className: "border-red-500 bg-red-50 text-red-900",
  },
  info: {
    icon: Info,
    className: "border-blue-500 bg-blue-50 text-blue-900",
  },
};

export function Toast({ toast, onDismiss }: ToastProps) {
  const config = variantConfig[toast.variant];
  const Icon = config.icon;

  useEffect(() => {
    if (toast.variant === TOAST_VARIANT_OBJ.SUCCESS || toast.variant === TOAST_VARIANT_OBJ.INFO) {
      const timer = setTimeout(() => onDismiss(toast.id), 5000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [toast.id, toast.variant, onDismiss]);

  return (
    <div
      className={`flex items-start gap-2 rounded-md border p-3 shadow-md ${config.className}`}
      role="alert"
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" />
      <p className="flex-1 text-sm">{toast.message}</p>
      {toast.retryAction !== undefined && (
        <Button
          size="sm"
          variant="outline"
          onClick={toast.retryAction}
        >
          Retry
        </Button>
      )}
      <Button
        size="icon"
        variant="ghost"
        onClick={() => onDismiss(toast.id)}
        aria-label="Dismiss"
      >
        <X className="h-3 w-3" />
      </Button>
    </div>
  );
}

interface ToastContainerProps {
  toasts: ToastData[];
  onDismiss: (id: string) => void;
}

export function ToastContainer({ toasts, onDismiss }: ToastContainerProps) {
  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 flex max-w-md flex-col gap-2">
      {toasts.map((toast) => (
        <Toast key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

let toastCounter = 0;

export function createToast(
  variant: ToastVariant,
  message: string,
  retryAction?: () => void,
): ToastData {
  toastCounter += 1;
  return {
    id: `toast-${toastCounter}`,
    variant,
    message,
    retryAction,
  };
}
